import React, { useCallback, lazy, memo } from 'react';
import { MinusIcon, PlusIcon, TrashIcon, HeartIcon, FileTextIcon, GlobeIcon, PackageIcon, GiftIcon, LayersIcon, InfoIcon } from 'lucide-react';
import { motion, useAnimation, PanInfo } from 'framer-motion';
export type CartItemType = 'single' | 'double' | 'discount' | 'prescription' | 'global' | 'bundle' | 'gift' | 'combo';
export interface CartSubItem {
  id: number;
  name: string;
  image: string;
  quantity?: number;
}
export interface CartItemData {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  quantity: number;
  image: string;
  type: CartItemType;
  requiresPrescription?: boolean;
  isGlobal?: boolean;
  bundleItems?: CartSubItem[];
  giftItem?: CartSubItem;
  comboItems?: CartSubItem[];
  brand?: string;
}
interface CartItemProps {
  item: CartItemData;
  onQuantityChange: (id: number, quantity: number) => void;
  onRemove: (id: number) => void;
  onSaveForLater?: (id: number) => void;
}
export const CartItem = memo(function CartItem({
  item,
  onQuantityChange,
  onRemove,
  onSaveForLater
}: CartItemProps) {
  const controls = useAnimation();
  const handleDecrease = useCallback(() => {
    if (item.quantity > 1) {
      onQuantityChange(item.id, item.quantity - 1);
    }
  }, [item.id, item.quantity, onQuantityChange]);
  const handleIncrease = useCallback(() => {
    onQuantityChange(item.id, item.quantity + 1);
  }, [item.id, item.quantity, onQuantityChange]);
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    onQuantityChange(item.id, value);
  }, [item.id, onQuantityChange]);
  const handleRemove = useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -50 || velocity < -500) {
      controls.start({
        x: -80
      });
    } else {
      controls.start({
        x: 0
      });
    }
  };
  const handleSwipeDelete = () => {
    controls.start({
      x: -window.innerWidth,
      opacity: 0
    }).then(() => {
      handleRemove();
    });
  };
  const totalPrice = (item.price * item.quantity).toFixed(2);
  const hasDiscount = item.originalPrice && item.originalPrice > item.price;
  return <div className="relative rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow" data-id="element-1736" data-mp="2msr28klcm6rx">
      {/* Background Delete Action (Mobile Only) */}
      <div className="absolute inset-y-0 right-0 w-20 flex items-center justify-center md:hidden bg-red-500" data-id="element-1737" data-mp="2dr9lr141432p">
        <button onClick={handleSwipeDelete} className="w-full h-full flex flex-col items-center justify-center text-white" aria-label="Delete item" data-id="element-1738" data-mp="28thjbd5rcyk9">
          <TrashIcon className="w-6 h-6 mb-1" data-id="element-1739" data-mp="70tckttwnszq" />
          <span className="text-xs font-medium" data-id="element-1740" data-mp="ek2l6kul7nnq">Delete</span>
        </button>
      </div>

      {/* Foreground Content */}
      <motion.div drag="x" dragConstraints={{
      left: -80,
      right: 0
    }} dragElastic={0.1} onDragEnd={handleDragEnd} animate={controls} className="relative z-10 bg-white p-3 md:p-5" data-id="element-1741" data-mp="ji48jhz1q950">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-1742" data-mp="3rqci8so1d0vd">
          {/* Product Image & Main Info */}
          <div className="flex gap-4 flex-1" data-id="element-1743" data-mp="k1olhlcv2e2">
            <div className="relative flex-shrink-0 w-24 h-24 md:w-32 md:h-32 bg-gray-50 rounded-xl overflow-hidden border border-gray-100" data-id="element-1744" data-mp="kt8vw9i5dp1">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" data-id="element-1745" data-mp="5lh7b3028wta" />
              {item.isGlobal && <div className="absolute top-1 left-1 bg-blue-600 text-white p-1 rounded-lg shadow-sm" data-id="element-1746" data-mp="1lwuywc46y2x9">
                  <GlobeIcon className="w-3 h-3" data-id="element-1747" data-mp="1ji69wp14iofz" />
                </div>}
            </div>

            <div className="flex-1 min-w-0 flex flex-col" data-id="element-1748" data-mp="1fhw53fp2i42p">
              <div className="flex flex-wrap gap-2 mb-1.5" data-id="element-1749" data-mp="1fjgy30vqheyg">
                {item.requiresPrescription && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold border border-amber-100 uppercase tracking-wider" data-id="element-1750" data-mp="1ohsob1souaqd">
                    <FileTextIcon className="w-3 h-3" data-id="element-1751" data-mp="22f7j6m1pnapq" />
                    Prescription Required
                  </span>}
                {item.type === 'bundle' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100 uppercase tracking-wider" data-id="element-1752" data-mp="2c9hiknp2l99y">
                    <PackageIcon className="w-3 h-3" data-id="element-1753" data-mp="2m3gj5i9hwkzs" />
                    Bundle Deal
                  </span>}
                {item.type === 'combo' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold border border-indigo-100 uppercase tracking-wider" data-id="element-1754" data-mp="30tkxqmxf877l">
                    <LayersIcon className="w-3 h-3" data-id="element-1755" data-mp="2qtaiyxvqmrqb" />
                    Combo Set
                  </span>}
                {item.type === 'double' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-bold border border-green-100 uppercase tracking-wider" data-id="element-1756" data-mp="2h3qyxowagrtv">
                    Buy 1 Get 1
                  </span>}
              </div>

              <h3 className="text-sm md:text-lg font-bold text-gray-900 line-clamp-2 leading-tight mb-1" data-id="element-1757" data-mp="2d2fjudb1jozx">
                {item.name}
              </h3>
              {item.brand && <p className="text-xs text-gray-500 mb-2" data-id="element-1758" data-mp="1y7r84hf1rubv">{item.brand}</p>}

              <div className="mt-auto flex items-center gap-3" data-id="element-1759" data-mp="236m0ob2tgx96">
                <div className="flex flex-col" data-id="element-1760" data-mp="1k0o540fudgh7">
                  <div className="flex items-center gap-2" data-id="element-1761" data-mp="1f966vk57dtzx">
                    <span className="text-base md:text-xl font-black text-[#1E2B55]" data-id="element-1762" data-mp="1f7lcpvmswd2l">
                      {item.price.toFixed(2)} SAR
                    </span>
                    {hasDiscount && <span className="text-xs md:text-sm text-gray-400 line-through font-medium" data-id="element-1763" data-mp="15ctcek1u3c44">
                        {item.originalPrice?.toFixed(2)} SAR
                      </span>}
                  </div>
                  {hasDiscount && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded w-fit mt-0.5" data-id="element-1764" data-mp="1szjcvgyjaymm">
                      Save {(item.originalPrice! - item.price).toFixed(2)} SAR
                    </span>}
                </div>
              </div>
            </div>
          </div>

          {/* Controls & Actions */}
          <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-4 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100" data-id="element-1765" data-mp="cono5nbkn4zj">
            <div className="flex flex-col md:items-end gap-1" data-id="element-1766" data-mp="2t90vudnig9en">
              <div className="flex items-center bg-gray-50 rounded-xl p-1 border border-gray-200" data-id="element-1767" data-mp="2y7kgmaqdbf5f">
                <button onClick={handleDecrease} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all disabled:opacity-30" disabled={item.quantity <= 1} data-id="element-1768" data-mp="32yu4p6w9qaf7">
                  <MinusIcon className="w-4 h-4 text-gray-600" data-id="element-1769" data-mp="3gxtvak3ccze0" />
                </button>
                <input type="number" value={item.quantity} onChange={handleInputChange} className="w-10 text-center bg-transparent font-bold text-sm focus:outline-none" min="1" data-id="element-1770" data-mp="3lx6gz1ih80f0" />
                <button onClick={handleIncrease} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:shadow-sm transition-all" data-id="element-1771" data-mp="1rl8h3q624gvs">
                  <PlusIcon className="w-4 h-4 text-gray-600" data-id="element-1772" data-mp="713eyosup8sn" />
                </button>
              </div>
              <p className="hidden md:block text-[10px] text-gray-400 font-medium mt-1" data-id="element-1773" data-mp="22jnk8vhn4sw">
                Unit Price: {item.price.toFixed(2)} SAR
              </p>
            </div>

            <div className="flex items-center gap-2" data-id="element-1774" data-mp="35j0a7j0j68xv">
              <button onClick={() => onSaveForLater?.(item.id)} className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-all group" title="Save for later" data-id="element-1775" data-mp="35ka92af7jl5k">
                <HeartIcon className="w-5 h-5 group-active:scale-90 transition-transform" data-id="element-1776" data-mp="zopenlyyjta8" />
              </button>
              <button onClick={handleRemove} className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all group" title="Remove item" data-id="element-1777" data-mp="uqnyb8sx282g">
                <TrashIcon className="w-5 h-5 group-active:scale-90 transition-transform" data-id="element-1778" data-mp="tatnfuqirlbf" />
              </button>
            </div>

            <div className="hidden md:block mt-auto text-right" data-id="element-1779" data-mp="xbn9eikeiq1o">
              <p className="text-xs text-gray-400 font-medium mb-0.5" data-id="element-1780" data-mp="1l1zivxvaouud">Total</p>
              <p className="text-xl font-black text-[#1E2B55]" data-id="element-1781" data-mp="1q0kgdpj5x0f4">
                {totalPrice} SAR
              </p>
            </div>
          </div>
        </div>

        {/* Sub-items (Bundles, Combos, Gifts) */}
        {(item.bundleItems || item.comboItems || item.giftItem) && <div className="mt-4 pt-4 border-t border-dashed border-gray-200" data-id="element-1782" data-mp="23ztvsmzjnqi1">
            <div className="flex items-center gap-2 mb-3" data-id="element-1783" data-mp="23xpnlsum45by">
              <InfoIcon className="w-4 h-4 text-gray-400" data-id="element-1784" data-mp="28rka141lqyeq" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1785" data-mp="x9gdo0w60k7n">
                Included in this {item.type === 'gift' ? 'order' : 'set'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-1786" data-mp="nb99yjqvzynl">
              {item.bundleItems?.map(sub => <div key={sub.id} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-1787" data-mp="s92cn2szeaj4">
                  <img src={sub.image} alt={sub.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1788" data-mp="j9xjyd43pbev" />
                  <div className="min-w-0" data-id="element-1789" data-mp="9jcnav7zmgyl">
                    <p className="text-[11px] font-medium text-gray-700 truncate" data-id="element-1790" data-mp="9kdzx44njbif">
                      {sub.name}
                    </p>
                    <p className="text-[10px] text-gray-400" data-id="element-1791" data-mp="3rrlcfgrq74nv">
                      Qty: {sub.quantity || 1}
                    </p>
                  </div>
                </div>)}

              {item.comboItems?.map(sub => <div key={sub.id} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-1792" data-mp="384h14rsubs1m">
                  <img src={sub.image} alt={sub.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1793" data-mp="3h4dh8515zil2" />
                  <div className="min-w-0" data-id="element-1794" data-mp="3r2b3wo3dl7bk">
                    <p className="text-[11px] font-medium text-gray-700 truncate" data-id="element-1795" data-mp="3r1i7afy4ry75">
                      {sub.name}
                    </p>
                    <p className="text-[10px] text-gray-400" data-id="element-1796" data-mp="8q4wtk8spy3z">
                      Included in Combo
                    </p>
                  </div>
                </div>)}

              {item.giftItem && <div className="flex items-center gap-3 bg-green-50 p-2 rounded-lg border border-green-100 col-span-full sm:col-span-1" data-id="element-1797" data-mp="dnn99km0uvgq">
                  <div className="relative" data-id="element-1798" data-mp="37go6ryhntibt">
                    <img src={item.giftItem.image} alt={item.giftItem.name} className="w-10 h-10 rounded object-cover bg-white" data-id="element-1799" data-mp="3bgycrb5gc8qo" />
                    <div className="absolute -top-1 -right-1 bg-green-500 text-white p-0.5 rounded-full shadow-sm" data-id="element-1800" data-mp="3l6hzwt076orc">
                      <GiftIcon className="w-2.5 h-2.5" data-id="element-1801" data-mp="3q6nscbeakfah" />
                    </div>
                  </div>
                  <div className="min-w-0" data-id="element-1802" data-mp="3v3wdmkuqkzfy">
                    <div className="flex items-center gap-1" data-id="element-1803" data-mp="3v71zlr82jl7g">
                      <span className="text-[9px] font-black text-green-600 uppercase tracking-tighter" data-id="element-1804" data-mp="32gchvlr5xhm">
                        Free Gift
                      </span>
                    </div>
                    <p className="text-[11px] font-bold text-gray-800 truncate" data-id="element-1805" data-mp="24tl1ejwawjy9">
                      {item.giftItem.name}
                    </p>
                  </div>
                </div>}
            </div>
          </div>}
      </motion.div>
    </div>;
});